import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';
import { FcMusic } from 'react-icons/fc';

export default function EditMusic() {
  const auth = getAuth();
  const navigate = useNavigate();
  const { musicId } = useParams();
  const [formData, setFormData] = useState({
    title: '',
  });
  const [musicFile, setMusicFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  // Check if user is authenticated and fetch music data
  useEffect(() => {
    if (!auth.currentUser) {
      toast.error('You must be logged in to edit music');
      navigate('/sign-in');
      return;
    }

    async function fetchMusicData() {
      try {
        const musicRef = doc(db, 'music', musicId);
        const musicSnap = await getDoc(musicRef);

        if (!musicSnap.exists()) {
          toast.error('Music not found');
          navigate('/profile');
          return;
        }

        const musicData = musicSnap.data();
        // Verify the user owns this music
        if (musicData.userRef !== auth.currentUser.uid) {
          toast.error('You are not authorized to edit this music');
          navigate('/profile');
          return;
        }

        setFormData({
          title: musicData.title || '',
        });
        setOriginalData(musicData);
      } catch (err) {
        console.error('Error fetching music data:', err);
        setError('Failed to load music data: ' + err.message);
        navigate('/profile');
      }
    }

    fetchMusicData();
  }, [auth.currentUser, musicId, navigate]);

  const handleChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (audio files only)
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid audio file (MP3, WAV, OGG)');
        setMusicFile(null);
        return;
      }
      setError(null);
      setMusicFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Double-check authentication before proceeding
    if (!auth.currentUser) {
      setError('You must be logged in to edit music');
      navigate('/sign-in');
      return;
    }

    // Validate inputs
    if (!formData.title) {
      setError('Please enter a title');
      return;
    }

    try {
      setUpdating(true);
      setUploadProgress(0);

      const musicRef = doc(db, 'music', musicId);
      let updatedData = {
        title: formData.title,
      };

      // If a new file is uploaded, handle the file replacement
      if (musicFile) {
        const storage = getStorage();
        const fileName = `${auth.currentUser.uid}-${Date.now()}-${musicFile.name}`;
        const storageRef = ref(storage, `music/${auth.currentUser.uid}/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, musicFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              console.error('Error uploading file:', error);
              setError('Failed to upload music file: ' + error.message);
              setUpdating(false);
              setUploadProgress(0);
              reject(error);
            },
            async () => {
              try {
                // Get the new download URL
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                updatedData.url = downloadURL;

                // Delete the old file from Storage
                if (originalData.url) {
                  const oldFileRef = ref(storage, originalData.url);
                  await deleteObject(oldFileRef).catch((error) => {
                    console.error('Error deleting old music file:', error);
                    // Continue even if deletion fails (file might already be deleted)
                  });
                }
                resolve();
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      }

      // Update Firestore document
      await updateDoc(musicRef, updatedData);
      toast.success('Music updated successfully!');
      setUpdating(false);
      setUploadProgress(0);
      setMusicFile(null);
      navigate('/profile');
    } catch (err) {
      console.error('Error during update process:', err);
      setError('Failed to update music: ' + err.message);
      setUpdating(false);
      setUploadProgress(0);
    }
  };

  // Prevent rendering until originalData is loaded
  if (!originalData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-900 rounded-xl shadow-md overflow-hidden">
          <div className="p-6 md:p-8">
            <h1 className="text-3xl font-bold text-white mb-8 text-center">
              Edit Music
            </h1>

            {error && (
              <div className="mb-6 text-center text-red-500">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Title Input */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">
                    Music Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter music title"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    disabled={updating}
                  />
                </div>

                {/* File Upload (Optional) */}
                <div>
                  <label htmlFor="musicFile" className="block text-sm font-medium text-gray-300 mb-1">
                    Replace Music File (MP3, WAV, OGG) - Optional
                  </label>
                  <input
                    type="file"
                    id="musicFile"
                    accept="audio/mpeg,audio/wav,audio/ogg"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-sky-400 file:text-white hover:file:bg-sky-500"
                    disabled={updating}
                  />
                  {musicFile ? (
                    <p className="mt-2 text-sm text-gray-400">
                      New file selected: {musicFile.name}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-gray-400">
                      Current file: {originalData.url.split('/').pop().split('?')[0]}
                    </p>
                  )}
                </div>

                {/* Upload Progress */}
                {updating && musicFile && (
                  <div className="w-full bg-gray-700 rounded-full h-4">
                    <div
                      className="bg-sky-400 h-4 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                    <p className="text-center text-sm text-gray-300 mt-2">
                      Uploading: {Math.round(uploadProgress)}%
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={updating}
                    className={`w-full bg-sky-400 text-white uppercase px-4 py-3 text-sm font-medium rounded-full shadow-md transition duration-150 ease-in-out ${
                      updating
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-sky-500 hover:shadow-lg active:bg-sky-600'
                    }`}
                  >
                    <div className="flex justify-center items-center">
                      <FcMusic className="mr-2 text-3xl bg-white rounded-full p-1 border-2" />
                      <span>{updating ? 'Updating...' : 'Update Music'}</span>
                    </div>
                  </button>
                </div>

                {/* Cancel Button */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/profile')}
                    className="mt-4 text-gray-400 hover:text-gray-300 font-medium transition"
                    disabled={updating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}